import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { looksLikeReference, parseReference, type ReferenceType } from '@aimmo/contracts';
import { Input, cn, toast } from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';

/** Where each kind of record lives. */
const ROUTES: Record<ReferenceType, string> = {
  mandate: '/incarichi',
  property: '/immobili',
  contact: '/contatti',
  demand: '/richieste',
  building: '/edifici',
  activity: '/agenda',
};

/**
 * Type a code, land on the record (M14).
 *
 * This is what makes a reference worth having: the moment it exists for is
 * somebody reading it down the phone, and a code that cannot be looked up is
 * decoration.
 *
 * It resolves rather than searches. The code carries its own type and its own
 * check digits, so a wrong one is refused here instead of returning a list of
 * near-misses — which for an identifier would be worse than nothing.
 */
export function ReferenceSearch({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const [value, setValue] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  async function resolve(): Promise<void> {
    const code = value.trim();
    if (code === '') return;

    if (!looksLikeReference(code) || !parseReference(code)) {
      // Says which of the two it is: a code that does not exist and a code
      // written down wrong need different things from the person holding it.
      toast.error('Codice non valido.', {
        description: 'Controlla le cifre finali: sono quelle che verificano il resto.',
      });
      return;
    }

    setBusy(true);
    try {
      const result = await api.references.resolve({ params: { reference: code } });
      if (result.status === 404) {
        toast.error('Nessun record con questo codice.');
        return;
      }
      const hit = assertOk(result).body;
      setValue('');
      await navigate({ to: `${ROUTES[hit.type]}/$id`, params: { id: hit.id } });
    } catch {
      toast.error('Ricerca non riuscita.');
    } finally {
      setBusy(false);
    }
  }

  if (collapsed) return null;

  return (
    <div className="relative px-2 pb-2">
      <Search
        className={cn(
          'pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted',
          busy && 'animate-pulse',
        )}
        aria-hidden
      />
      <Input
        value={value}
        aria-label="Vai al riferimento"
        placeholder="Riferimento…"
        className="pl-8"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') void resolve();
        }}
      />
    </div>
  );
}
